import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import { useGetOffersQuery } from "services/offersApiService";
import { AppDispatch } from "state/reducers-combiner";
import { userState } from "state/user-slice";
import { setLoader } from "state/config-slice";
import { offersState, setNeedsRefetching, setOffers } from "state/offers-slice";
import getFeature from "utils/getFeature";
import Offer, { Status } from "types/Offer";

export default function useOffersRefreshing(cdnUrl: string) {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector(userState);
  const { needsRefetching } = useSelector(offersState);

  const pollingFeature = getFeature("polling");
  const pollingInterval = pollingFeature?.active ? pollingFeature.options?.intervalBetweenRequests : 3000;
  const pollingMaxCumulativeTime = pollingFeature?.active ? pollingFeature.options?.maxCumulativeTime : 600000;

  const [cumulativeTime, setCumulativeTime] = useState<number>(0);
  const [hasProvisioningOffers, setHasProvisioningOffers] = useState<boolean>(false);

  const refetchingCondition = user.loggedIn && hasProvisioningOffers && cumulativeTime < pollingMaxCumulativeTime;

  const { data, refetch, isFetching } = useGetOffersQuery("offers", {
    pollingInterval: refetchingCondition ? pollingInterval : undefined,
    refetchOnMountOrArgChange: true,
    skip: !user.loggedIn,
  });

  useEffect(() => {
    if (data && data.available && data.purchased && !isFetching) {
      dispatch(setOffers({ data, cdnUrl }));
      dispatch(setLoader({ showLoader: false, messageHeader: "", messageText: "" }));
      dispatch(setNeedsRefetching(false));

      setCumulativeTime((cumulativeTime) => cumulativeTime + pollingInterval);

      const newOffers = [...data.available, ...data.purchased];
      const stillHasProvisioningOffers =
        newOffers.filter(({ marketplaceInfo }: Offer) => marketplaceInfo.status === Status.provisioning).length > 0;

      setHasProvisioningOffers(stillHasProvisioningOffers);
    }
  }, [data, dispatch, isFetching, cdnUrl, pollingInterval, user.mustSelectContract]);

  useEffect(() => {
    if (needsRefetching) {
      setCumulativeTime(0);
      setHasProvisioningOffers(true);
      dispatch(setLoader({ showLoader: true, messageHeader: "", messageText: "" }));
      refetch();
    }
  }, [dispatch, needsRefetching, refetch]);

  return;
}
